<template>
    <div>
        <div class="row gutter-xs">
            <div class="panel panel-default">
                <div class="panel-body">
                    <div class="flex flex-col items-center justify-between md:flex-row">
                        <div class="hidden lg:block lg:w-1/3"></div>
                        <div class="flex justify-center lg:justify-center md:justify-end md:w-1/3 w-full">
                            <div class="form-group form-group-sm">
                                <div class="input-with-icon mx-auto w-300px">
                                    <input class="form-control input-sm" type="search" v-model="searchQuery" :placeholder="$t('updates.kiosk_search_placeholder')">
                                    <span class="icon icon-search input-icon"></span>
                                </div>
                            </div>
                        </div>
                        <div class="mb-6 md:mb-0 md:mt-0 mt-2 md:w-1/3 self-start w-full">
                            <div class="flex justify-center md:justify-end">
                                <a href="/kiosk/updates/create" class="btn btn-primary btn-sm btn-labeled">
                                    <span class="btn-label">
                                        <span class="icon icon-plus icon-lg icon-fw"></span>
                                    </span>
                                    {{ $t('updates.new_update') }}
                                </a>
                            </div>
                        </div>
                    </div>
                    <vue-good-table
                        mode="remote"
                        @on-page-change="onPageChange"
                        @on-sort-change="onSortChange"
                        @on-per-page-change="onPerPageChange"
                        @on-search="onSearchChange"
                        :search-options="{ enabled: true, externalQuery: serverParams.search }"
                        :sort-options="{
                            enabled: true,
                            initialSortBy: { field: 'created_at', type: 'desc' },
                        }"
                        :pagination-options="{
                            enabled: true,
                            perPage: 20,
                            setCurrentPage: table.currentPage,
                            nextLabel: $t('tables.next'),
                            prevLabel: $t('tables.previous'),
                            rowsPerPageLabel: $t('tables.rows_per_page'),
                            ofLabel: $t('tables.of'),
                            pageLabel: $t('tables.page'),
                            allLabel: $t('tables.all'),
                        }"
                        :columns="table.columns"
                        :rows="table.rows"
                        :total-rows="table.totalRecords"
                        :is-loading="table.isLoading"
                        style-class="table vgt-table striped">
                        <div slot="emptystate">
                            <div class="row text-center p-y-md">
                                <div class="col-xs-8 col-xs-offset-2">
                                    <p>{{ $t('tables.no_records_found') }}</p>
                                </div>
                            </div>
                        </div>
                        <template slot="table-row" slot-scope="props">
                            <template v-if="props.column.field === 'title'">
                                <div class="flex items-center">
                                    <div class="mr-3">
                                        <template v-if="props.row.application.slug === 'crm'">
                                            <div class="bg-purple-500 flex h-8 items-center justify-center mr-2 rounded-full w-8" style="padding: 0.46rem;">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="block fill-current h-6 text-white w-6">
                                                    <path d="M8 24l3-9h-9l14-15-3 9h9l-14 15z"></path>
                                                </svg>
                                            </div>
                                        </template>
                                        <template v-else-if="props.row.application.slug === 'tasks'">
                                            <div class="bg-red-500 flex h-8 items-center justify-center mr-2 rounded-full w-8" style="padding: 0.46rem;">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="block fill-current h-8 text-white w-8">
                                                    <path d="M4.967 15c-2.743 0-4.967-2.238-4.967-5 0-2.761 2.224-5 4.967-5h5.033v10h-5.033zm7.04 7.475c-.52-.424-.902-.994-1.095-1.637l-1.151-3.827h-5.125l1.905 5.883c.214.659.828 1.106 1.521 1.106h3.439c.358 0 .677-.225.797-.562.12-.337.015-.713-.263-.939l-.028-.024zm-.007-17.475v10c3.164.509 7.161 1.992 12 5v-20c-4.964 3.085-8.876 4.502-12 5z"></path>
                                                </svg>
                                            </div>
                                        </template>
                                        <template v-else-if="props.row.application.slug === 'budgets'">
                                            <div class="bg-blue-500 flex h-8 items-center justify-center mr-2 rounded-full w-8" style="padding: 0.46rem;">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="block fill-current h-8 text-white w-8">
                                                    <path d="M5 19h-4v-4h4v4zm6 0h-4v-8h4v8zm6 0h-4v-13h4v13zm6 0h-4v-19h4v19zm1 2h-24v2h24v-2z"></path>
                                                </svg>
                                            </div>
                                        </template>
                                    </div>
                                    <div class="flex flex-col">
                                        <div class="font-bold" v-html="props.row.locale_content.title"></div>
                                        <div class="font-medium text-gray-table-light text-xs" :set="localesEnum = getUpdateLocalesEnum(props.row)">
                                            {{ `(${$t('updates.languages')}: ${localesEnum})` }}
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <template v-if="props.column.field === 'created_at'">
                                <div class="flex flex-col">
                                    <div>
                                        {{ formatDate(props.row.created_at) }}
                                    </div>
                                    <div class="font-medium text-2xs text-gray-table-light">
                                        ({{ props.row.readable_created_at }})
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="props.column.field == 'actions'">
                                <div class="flex items-center justify-center space-x-2">
                                    <a :href="`/kiosk/updates/${props.row.id}/edit`" class="btn btn-default btn-xs" v-tooltip="{ content: $t('globals.edit'), placement: 'top' }">
                                        <span class="icon icon-pencil"></span>
                                    </a>
                                    <button @click="deleteUpdate(props.row)" class="btn btn-outline-danger btn-xs" v-tooltip="{ content: $t('globals.delete'), placement: 'top' }">
                                        <span class="icon icon-trash"></span>
                                    </button>
                                </div>
                            </template>
                            <span v-else v-html="props.formattedRow[props.column.field]"></span>
                        </template>
                    </vue-good-table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { lightFormat, parseISO } from 'date-fns';

export default {
    props: {
        initialSort: {
            type: Array,
            default: function() {
                return [
                    { column: 'created_at', dir: 'desc' },
                ];
            },
        },
    },

    data() {
        return {
            table: {
                columns: [
                    {
                        label: this.$t('updates.title'),
                        field: 'title',
                        sortable: false,
                    },
                    {
                        label: this.$t('updates.created_at'),
                        field: 'created_at',
                        html: true,
                    },
                    {
                        label: this.$t('globals.actions'),
                        field: 'actions',
                        html: true,
                        sortable: false,
                        thClass: 'text-center',
                    },
                ],
                rows: [],
                totalRecords: 0,
                currentPage: 1,
                isLoading: false,
            },
            serverParams: {
                order: [],
                start: 0,
                length: 20,
                search: '',
            },
            searchQuery: '',
        };
    },

    watch: {
        searchQuery: Globals.debounce(function (value) {
            this.serverParams.search = value;
        }, 750)
    },

    methods: {
        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },

        onPageChange(params) {
            this.table.currentPage = params.currentPage;
            let start = 0;
            if (params.currentPage > 1) {
                start = (params.currentPage - 1) * params.currentPerPage;
            }
            this.updateParams({ start: start });
            this.loadItems();
        },

        onPerPageChange(params) {
            this.updateParams({ length: params.currentPerPage });
            this.loadItems();
        },

        onSortChange(params) {
            const newOrder = [];
            newOrder.push({ 'column': _.head(params)['field'], 'dir': _.head(params)['type'] });
            this.updateParams({ order: newOrder });
            this.loadItems();
        },

        onSearchChange(params) {
            this.updateParams({ search: params.searchTerm });
            this.loadItems();
        },

        goToFirstPage() {
            this.table.currentPage = 1;
            this.updateParams({ start: 0 });
            this.loadItems();
        },

        loadItems() {
            this.table.isLoading = true;

            const queryParams = Object.assign({}, this.serverParams);

            axios.get('/data/kiosk/updates', { params: queryParams })
                .then(response => {
                    this.table.totalRecords = response.data.recordsFiltered;
                    this.table.rows = response.data.data;
                    this.table.isLoading = false;
                });
        },

        deleteUpdate(update) {
            Bus.$emit('showDeleteUpdateModal', { update: update });
        },

        getUpdateLocalesEnum(update) {
            return update.contents
                .map(content => content.locale_locale.toLowerCase())
                .join(', ');
        },

        formatDate(date) {
            return lightFormat(parseISO(date), 'dd-MM-y HH:mm');
        },
    },
};
</script>
